import React, { useState } from 'react';
import {
  Download,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Printer,
  X,
  FileText,
  Image as ImageIcon
} from 'lucide-react';

export interface MedicalDocument {
  id: string;
  title: string;
  type: 'pdf' | 'image' | 'report' | 'lab' | 'prescription' | 'discharge';
  date: string;
  provider: string;
  url: string;
  thumbnailUrl?: string;
  description?: string;
  pages?: number;
}

interface DocumentViewerProps {
  document: MedicalDocument;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFullScreen?: boolean;
  className?: string;
}

/**
 * Component for viewing medical documents with zoom, print, and download capabilities
 */
const DocumentViewer: React.FC<DocumentViewerProps> = ({
  document,
  onClose,
  onNext,
  onPrevious,
  isFullScreen = false,
  className = '',
}) => {
  const [zoom, setZoom] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 25, 50));
  };

  const handleNextPage = () => {
    if (document.pages && currentPage < document.pages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handlePrint = () => {
    window.open(document.url, '_blank');
  };

  const handleDownload = () => {
    const link = window.document.createElement('a');
    link.href = document.url;
    link.download = document.title;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  };

  const getDocumentTypeIcon = () => {
    switch (document.type) {
      case 'pdf':
        return <FileText className="w-5 h-5 text-[var(--status-error)]" />;
      case 'image':
        return <ImageIcon className="w-5 h-5 text-[var(--brand-secondary)]" />;
      case 'report':
        return <FileText className="w-5 h-5 text-[var(--brand-primary)]" />;
      case 'lab':
        return <FileText className="w-5 h-5 text-[var(--clinical-glucose)]" />;
      case 'prescription':
        return <FileText className="w-5 h-5 text-[var(--clinical-heart)]" />;
      case 'discharge':
        return <FileText className="w-5 h-5 text-[var(--clinical-blood)]" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div 
      className={`
        bg-[var(--background-surface)] 
        ${isFullScreen ? 'fixed inset-0 z-50' : 'rounded-lg border border-[var(--border-subtle)] shadow-lg'} 
        flex flex-col
        ${className}
      `}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[var(--border-subtle)]">
        <div className="flex items-center">
          {getDocumentTypeIcon()}
          <div className="ml-3">
            <h3 className="font-medium text-[var(--text-primary)]">{document.title}</h3>
            <p className="text-sm text-[var(--text-secondary)]">
              {formatDate(document.date)} • {document.provider}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            className="p-2 text-[var(--text-secondary)] hover:bg-[var(--background-subtle)] rounded-full"
            onClick={handlePrint}
            aria-label="Print document"
          >
            <Printer className="w-5 h-5" />
          </button>
          
          <button
            className="p-2 text-[var(--text-secondary)] hover:bg-[var(--background-subtle)] rounded-full"
            onClick={handleDownload}
            aria-label="Download document"
          >
            <Download className="w-5 h-5" />
          </button>
          
          <button
            className="p-2 text-[var(--text-secondary)] hover:bg-[var(--background-subtle)] rounded-full"
            onClick={onClose}
            aria-label="Close document viewer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {/* Document Controls */}
      <div className="flex items-center justify-between p-2 bg-[var(--background-subtle)]">
        <div className="flex items-center space-x-1">
          <button
            className="p-1.5 text-[var(--text-secondary)] hover:bg-[var(--background-elevated)] rounded-md disabled:opacity-50"
            onClick={handleZoomOut}
            disabled={zoom <= 50}
            aria-label="Zoom out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          
          <span className="text-sm text-[var(--text-secondary)] px-2">{zoom}%</span>
          
          <button
            className="p-1.5 text-[var(--text-secondary)] hover:bg-[var(--background-elevated)] rounded-md disabled:opacity-50"
            onClick={handleZoomIn}
            disabled={zoom >= 200}
            aria-label="Zoom in"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
        </div>
        
        {document.pages && document.pages > 1 && (
          <div className="flex items-center space-x-1">
            <button
              className="p-1.5 text-[var(--text-secondary)] hover:bg-[var(--background-elevated)] rounded-md disabled:opacity-50"
              onClick={handlePreviousPage}
              disabled={currentPage <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            
            <span className="text-sm text-[var(--text-secondary)] px-2">
              {currentPage} / {document.pages}
            </span>
            
            <button
              className="p-1.5 text-[var(--text-secondary)] hover:bg-[var(--background-elevated)] rounded-md disabled:opacity-50"
              onClick={handleNextPage}
              disabled={currentPage >= (document.pages || 1)}
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
        
        <div className="flex items-center space-x-1">
          {onPrevious && (
            <button
              className="p-1.5 text-[var(--text-secondary)] hover:bg-[var(--background-elevated)] rounded-md"
              onClick={onPrevious}
              aria-label="Previous document"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
          
          {onNext && (
            <button
              className="p-1.5 text-[var(--text-secondary)] hover:bg-[var(--background-elevated)] rounded-md"
              onClick={onNext}
              aria-label="Next document"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      {/* Document Viewer */}
      <div 
        className="flex-1 overflow-auto bg-[var(--background-subtle)] flex items-center justify-center p-4"
        style={{ minHeight: "400px" }}
      >
        {isLoading && (
          <div className="flex flex-col items-center justify-center text-[var(--text-secondary)]">
            <div className="w-8 h-8 border-4 border-[var(--border-subtle)] border-t-[var(--brand-primary)] rounded-full animate-spin mb-4"></div>
            <p>Loading document...</p>
          </div>
        )}
        
        {document.type === 'image' ? (
          <img
            src={document.url}
            alt={document.title}
            className="max-w-full object-contain"
            style={{ transform: `scale(${zoom / 100})` }}
            onLoad={() => setIsLoading(false)}
          />
        ) : (
          <iframe
            src={document.url}
            title={document.title}
            className={`w-full h-full ${isLoading ? 'hidden' : 'block'}`}
            onLoad={() => setIsLoading(false)}
          />
        )}
      </div>
      
      {/* Footer */}
      {document.description && (
        <div className="p-3 text-sm text-[var(--text-secondary)] border-t border-[var(--border-subtle)]">
          {document.description}
        </div>
      )}
    </div>
  );
};

export default DocumentViewer; 